import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../../context/AuthContext';
import { toast } from 'react-toastify';

interface ProjectFile {
    id: number;
    filepath: string;
    projectId: number;
}

interface MemberListSectionProps {
    projectFile: ProjectFile;
    projectFileFilter: string;
    roundTableNumber: number;
}

const MemberListSection: React.FC<MemberListSectionProps> = ({ projectFile, projectFileFilter, roundTableNumber }) => {
    const authContext = useContext(AuthContext);
    const [memberData, setMemberData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [filterSettings, setFilterSettings] = useState<any>(null);

    if (!authContext) {
        throw new Error('MemberListSection must be used within an AuthProvider');
    }

    const { token } = authContext;

    const getFilenameFromPath = (filepath: string): string => {
        return filepath.split('/').pop() || filepath;
    };

    const handleDownloadFile = async () => {
        try {
            const response = await fetch(`/api/projects/files/${projectFile.id}/download`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Download fehlgeschlagen');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = getFilenameFromPath(projectFile.filepath);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            toast.success('Datei heruntergeladen');
        } catch (error) {
            console.error('Error downloading file:', error);
            toast.error(error instanceof Error ? error.message : 'Fehler beim Herunterladen');
        }
    };

    useEffect(() => {
        const loadMemberData = async () => {
            setLoading(true);
            try {
                // Parse the filter settings
                const parsedSettings = JSON.parse(projectFileFilter);
                setFilterSettings(parsedSettings);
                
                const { statusColumns, clubNumberColumn } = parsedSettings;

                // Fetch and parse the project file
                const response = await fetch(`/api/projects/files/${projectFile.id}/parse`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to parse Excel file');
                }

                const result = await response.json();
                
                // Filter the data based on the stored settings
                let filteredData = result.data;

                // Filter by club number
                if (clubNumberColumn) {
                    filteredData = filteredData.filter((row: any) => {
                        const clubNumber = Number(row[clubNumberColumn]);
                        return clubNumber === roundTableNumber;
                    });
                }

                // Find the status column (case-insensitive)
                const statusColumn = result.columns.find((col: string) =>
                    col.toLowerCase().includes('status')
                );

                // Apply status filter if we have status columns selected
                if (statusColumns && statusColumns.length > 0 && statusColumn) {
                    filteredData = filteredData.filter((row: any) => {
                        const statusValue = String(row[statusColumn] || '');
                        return statusColumns.includes(statusValue);
                    });
                }

                setMemberData(filteredData);
            } catch (error) {
                console.error('Error loading member data:', error);
                toast.error('Fehler beim Laden der Mitgliederdaten');
            } finally {
                setLoading(false);
            }
        };

        loadMemberData();
    }, [projectFile.id, projectFileFilter, token, roundTableNumber]);

    return (
        <div className="member-list-section">
            <h2>Mitgliederliste</h2>
            <div className="member-list-card">
                <div className="file-info-section">
                    <p>
                        <strong>Datei:</strong> {getFilenameFromPath(projectFile.filepath)}{' '}
                        <button
                            className="download-file-btn"
                            onClick={handleDownloadFile}
                            title="Datei herunterladen"
                        >
                            ⬇ Download
                        </button>
                    </p>
                    {filterSettings && (
                        <div className="filter-info">
                            <p><strong>Filter-Einstellungen:</strong></p>
                            <ul>
                                <li><strong>Clubnummer-Spalte:</strong> {filterSettings.clubNumberColumn || 'Nicht festgelegt'}</li>
                                {filterSettings.statusColumns && filterSettings.statusColumns.length > 0 && (
                                    <li><strong>Status-Filter:</strong> {filterSettings.statusColumns.join(', ')}</li>
                                )}
                            </ul>
                        </div>
                    )}
                </div>
                
                {loading ? (
                    <div className="loading-members">Lade Mitgliederdaten...</div>
                ) : memberData.length > 0 ? (
                    <div className="members-table-wrapper">
                        <p className="member-count"><strong>Anzahl Mitglieder:</strong> {memberData.length}</p>
                        <table className="members-table">
                            <thead>
                                <tr>
                                    <th>Vorname</th>
                                    <th>Nachname</th>
                                </tr>
                            </thead>
                            <tbody>
                                {memberData.map((member, idx) => (
                                    <tr key={idx}>
                                        <td>{String(member['Vorname'] || '')}</td>
                                        <td>{String(member['Nachname'] || '')}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="no-members">
                        <p>Keine Mitglieder gefunden</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MemberListSection;
